import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, ChevronRight, Sparkles } from "lucide-react";
import { eventApi } from "@/lib/eventHub";
import EventLayout from "@/components/event/EventLayout";

/**
 * Public "Meet the Sponsors + VIP Panel" list page.
 * Renders profiles grouped by kind (sponsor / partner / panelist / speaker / trailblazer).
 */
export default function SponsorList() {
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let m = true;
    (async () => {
      try {
        const { data } = await eventApi.profiles();
        if (m) setProfiles(data.profiles || []);
      } catch { /* noop */ }
      if (m) setLoading(false);
    })();
    return () => { m = false; };
  }, []);

  const grouped = useMemo(() => {
    const g = {};
    for (const p of profiles) {
      const kind = p.kind || "sponsor";
      (g[kind] ||= []).push(p);
    }
    return g;
  }, [profiles]);

  const KIND_ORDER = ["sponsor", "partner", "panelist", "speaker", "trailblazer"];
  const KIND_LABELS = {
    sponsor: "Sponsors",
    partner: "Community Partners",
    panelist: "VIP Panel",
    speaker: "Speakers",
    trailblazer: "Trailblazer Honorees",
  };

  return (
    <EventLayout showProgramBanner={false} className="bg-gradient-to-b from-white via-emerald-50/40 to-white">
      <div className="mx-auto max-w-lg px-5 py-8">
        <div className="inline-flex items-center gap-1.5 rounded-full bg-fuchsia-50 border border-fuchsia-200 px-3 py-1 mb-3">
          <Sparkles size={12} className="text-fuchsia-700" />
          <span className="text-[10px] tracking-[0.22em] uppercase font-[Outfit] font-bold text-fuchsia-800">Meet the Community</span>
        </div>
        <h1 className="font-[Cormorant_Garamond] text-4xl leading-[1.02] text-emerald-950">
          Sponsors, Partners, and <span className="italic text-fuchsia-700">VIP Panel</span>
        </h1>
        <p className="mt-2 font-[Outfit] text-sm text-emerald-950/60 leading-relaxed">
          Every business shower is powered by community. Meet the leaders investing in the next class of small-business honorees.
        </p>

        {loading ? (
          <div className="p-12 text-center text-emerald-800/50"><Loader2 className="mx-auto animate-spin" /></div>
        ) : profiles.length === 0 ? (
          <div className="mt-6 rounded-2xl border-2 border-dashed border-emerald-950/10 bg-white p-8 text-center">
            <div className="font-[Cormorant_Garamond] text-2xl text-emerald-950">Profiles coming soon</div>
            <p className="text-sm font-[Outfit] text-emerald-800/60 mt-1">Administrators can add profiles from the admin dashboard.</p>
          </div>
        ) : (
          <div className="mt-6 space-y-6">
            {KIND_ORDER.filter((k) => (grouped[k] || []).length > 0).map((kind) => (
              <section key={kind} data-testid={`sponsors-group-${kind}`}>
                <h2 className="text-[10px] uppercase tracking-[0.28em] font-[Outfit] font-bold text-fuchsia-700 mb-2">
                  {KIND_LABELS[kind] || kind}
                </h2>
                <ul className="space-y-2">
                  {(grouped[kind] || []).map((p) => (
                    <li key={p.id}>
                      <Link
                        to={`/event/sponsor/${p.slug}`}
                        data-testid={`sponsor-card-${p.slug}`}
                        className="block rounded-2xl border-2 border-emerald-100 bg-white hover:border-fuchsia-300 hover:bg-fuchsia-50/40 px-4 py-3 flex items-center gap-3 transition-colors"
                      >
                        {p.logo_url ? (
                          <img src={p.logo_url} alt={p.name} className="h-11 w-11 rounded-lg object-contain bg-white ring-1 ring-emerald-100" />
                        ) : (
                          <div className="h-11 w-11 rounded-lg bg-emerald-100 flex items-center justify-center font-[Cormorant_Garamond] text-emerald-800 text-xl">
                            {(p.name || "?").charAt(0)}
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="font-[Cormorant_Garamond] text-xl text-emerald-950 leading-tight truncate">{p.name}</div>
                          <div className="text-xs font-[Outfit] text-emerald-950/60 truncate">
                            {p.sponsor_level || p.organization}
                          </div>
                        </div>
                        <ChevronRight size={16} className="text-emerald-950/30 shrink-0" />
                      </Link>
                    </li>
                  ))}
                </ul>
              </section>
            ))}
          </div>
        )}
      </div>
    </EventLayout>
  );
}
